import React, { useState, useEffect, useRef } from "react";
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "@chakra-ui/react";
import {
  Input,
  FormControl,
  FormLabel,
  Textarea,
  Box,
} from "@chakra-ui/react";
import ReactSelect from "react-select";
// Date picker
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from  "react-datepicker";
import es from 'date-fns/locale/es';
// Main components
import MainLayout from "../../../components/main/layout/layout";
// Common components
import CommonCheckbox from "../../../components/common/checkbox/checkbox";
// Maintenance components
import MaintenanceForm from "../../../components/maintenance/form/form";
// Components
import Upload from "../../../components/upload/upload";
import DynamicEditor from "../../../components/dynamicEditor/dynamicEditor";
// Actions
import * as mainActions from "../../../redux/actions/main";
import * as newsActions from "../../../redux/actions/news";
import * as filesActions from "../../../redux/actions/files";
import { getToastOpt } from "../../../utils/utils";
// Styles
import "./form.css";

// Vars
registerLocale('es', es)

const NewsForm = ({ isNew }) => {
  const dispatch = useDispatch();
  const childRef = useRef();
  const toast = useToast();
  // States
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({
    // "enabled": true,
  });
  const [isDirty, setIsDirty] = useState(false);
  const [isDirtyFiles, setIsDirtyFiles] = useState(false);
  const [files_1, setFiles_1] = useState([]);
  // Selectors
  const currentPage = useSelector((state) => state.mainReducer.currentPage);
  const categories = useSelector((state) => state.newsReducer.categories);

  // Set constants
  const page = "news";
  const primaryName = !isNew && !!form[`title`] ? form[`title`] : ``;
  const itemNameSingular = "Notícia";

  // Get id from url
  let id = null;
  const { id: paramId } = useParams();
  if (!isNew) {
    id = parseInt(paramId);
  }

  useEffect(() => {

    // Get categories
    if (categories.length == 0) {
      setIsLoading(true);
      dispatch(newsActions.getCategories()).then(
        (response) => {
          setIsLoading(false);
        },
        (err) => {
          setIsLoading(false);
          toast(getToastOpt({
            title: `Error obtenint categories de notícies`,
            description: err,
            status: "error",
          }));
        }
      );      
    }

    if (!!currentPage) return;

    // Set active page
    dispatch(mainActions.setCurrentPage(page));

    // Set initial breadscrum
    if (isNew) {
      const pathKey = 'new';
      const path = `${page}/${pathKey}`;
      dispatch(mainActions.setBreadscrum(page, [{ path, pathKey: 'new-m', itemNameSingular, }]));
    } else {
      const pathKey = 'edit';
      const path = `${page}/${pathKey}/${id}`;
      dispatch(mainActions.setBreadscrum(page, [{ path, pathKey, primaryValue: id, primaryName, itemNameSingular, }]));
    }
  }, []);

  const handleChange = (evt) => {
    if (childRef.current) {
      childRef.current.handleChange(evt);
    }
  };

  const getFormError = () => {
    // Validar que existan items en json_content
    if (!form['json_content'] || form['json_content'].length === 0) {
      return `Afegiu almenys un item al contingut dinàmic`;
    }

    // Validar cada item del json_content
    for (let i = 0; i < form['json_content'].length; i++) {
      const item = form['json_content'][i];

      // Validar según el tipo
      if (item.type === 'text') {
        if (!item.description || item.description.trim() === '') {
          return `L'item ${i + 1} (Texte) necessita un valor`;
        }
      } else if (item.type === 'audio') {
        if (isNew && (!item.files || item.files.length === 0)) {
          return `L'item ${i + 1} (Àudio) necessita un fitxer`;
        }
      } else if (item.type === 'image') {
        if (isNew && (!item.files || item.files.length === 0)) {
          return `L'item ${i + 1} (Imatge) necessita un fitxer`;
        }
      } else if (item.type === 'link') {
        if (!item.url) {
          return `L'item ${i + 1} (Enllaç) necessita un valor`;
        }
      }
    }
  };

  const renderForm = () => {
    return (
      <div className="news-form-wrapper">
        {renderMainCard()}
        <div className="maintenance-form-card news-form-secondcard">
          {renderDynamicEditor()}
        </div>
      </div>
    );
  };

  const renderMainCard = () => {
    let title;
    if (isNew) {
      title = "Nova Notícia";
    } else {
      title = `Notícia${!!form[`id`] ? ` amb Id: ${form[`id`]}` : ''}`;
    }
    
    // Set options for category combo
    const categoryOptions = []; 
    for (var i = 0; i < categories.length; i++) { 
      const category = categories[i];
      categoryOptions.push({ label: category, value: category});
    }

    return (
      <div className="news-form-maincard">
        <div className="title maintenance-form-title">
          {title}
        </div>  
        {/* IMPORTANT !!! Els checkboxes no poden estar en el mateix FormControl En producció no funciona!! */}
        <CommonCheckbox
          isChecked={!!form['enabled']}
          onChange={(e) => {
            setForm({
              ...form,
              "enabled": e.target.checked,
            });     
            setIsDirty(true);         
          }}
        >
          Habilitat
        </CommonCheckbox>
        <FormControl isRequired mt={4}>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            Títol
          </FormLabel>
          <Input
            className="maintenance-form-field"
            placeholder="Títol"
            name="title"
            data-type={`string`}
            onChange={handleChange}
            value={form[`title`] ? form[`title`] : ``}
            maxLength={255}
          />
        </FormControl>
        {renderUploadCard_1()}          
        <FormControl isRequired mt={4}>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            URL web
          </FormLabel>
          <Input
            className="maintenance-form-field"
            placeholder="https://"
            name="url_web"
            data-type={`string`}
            type="url"
            onChange={handleChange}
            value={form[`url_web`] ? form[`url_web`] : ``}
            maxLength={1024}
          />
        </FormControl>
        <Box 
          display="flex" 
          flexDirection="row" 
          gap={4}
        >
          <FormControl mt={4} isRequired w="150px" >
            <FormLabel fontSize="sm" className="maintenance-form-label">
              Data
            </FormLabel>
            <DatePicker 
                locale="es"
                dateFormat="dd/MM/yy"             
                selected={!!form[`date`] ? new Date(form[`date`]) : ''} 
                // onSelect={(date) => {                           
                // }} //when day is clicked
                onChange={(date) => { 
                    handleChange({
                      isDate: true, 
                      onlyDate: true,
                      fieldName: 'date', 
                      value: date,
                    })            
                }} 
                customInput={
                  <Input
                    className="maintenance-form-field"
                    textAlign="center"
                  />
                }
                placeholderText="Data" 
                className="custom-datepicker"
                portalId="root-portal" // Esto forzará el renderizado en el portal
                popperPlacement="bottom-start" // Ajusta la posición del calendario
                disabled={false}
            />
          </FormControl>              
          <FormControl w="200px" mt={4} >
            <FormLabel fontSize="sm" className="maintenance-form-label">
              Prioritat
            </FormLabel>
            <Input
                className="maintenance-form-field"     
                placeholder="Prioritat"
                name={`priority`}
                data-type={`integer`}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  if (!isNaN(inputValue)) {
                    handleChange(e);
                  }
                }}
                value={form[`priority`] ? form[`priority`] : ``}
                inputMode="numeric"
                w="120px"
                textAlign="left"
                type="number"
                min={1}
            />
          </FormControl>                     
        </Box>
        <FormControl mt={4}>
          <FormLabel fontSize="sm" className='maintenance-form-label'>
              Categoria
          </FormLabel>
          <ReactSelect
            className="maintenance-form-combo"
            classNamePrefix="react-select"
            name="category"
            options={categoryOptions}
            isSearchable
            isClearable
            value={categoryOptions.find(opt => opt.value === form["category"]) || null}
            onChange={(selectedOption) => {
                setForm(prev => ({
                    ...prev,
                    category: selectedOption ? selectedOption.value : null
                }));
                setIsDirty(true);
            }}
            placeholder="Categoria"
          />
        </FormControl> 
        <FormControl mt={4}>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            Autor
          </FormLabel>
          <Input
            className="maintenance-form-field"
            placeholder="Autor"
            name="author"
            data-type={`string`}
            onChange={handleChange}
            value={form[`author`] ? form[`author`] : ``}
            maxLength={255}
          />
        </FormControl>        
        <FormControl mt={4}>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            Sub-títol
          </FormLabel>
          <Textarea
            className="maintenance-form-field"
            placeholder="Sub-títol"
            name="subtitle"
            data-type={`string`}
            onChange={handleChange}
            value={form[`subtitle`] ? form[`subtitle`] : ``}
            minHeight="150px"
          />              
        </FormControl>  
      </div>   
    );
  };

  const renderUploadCard_1 = () => {
    return (
      <div className="news-form-upload-card-1">        
        <FormControl>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            Imatge principal
          </FormLabel>    
        </FormControl>     
        <Upload 
          type={"news"}
          id={id}
          id2={"1"}
          style={{ marginTop: "10px"}}
          onChange={(files) => {
            setFiles_1(files);        
          }}      
          onNewFile={(newFiles) => {
            setIsDirty(true);
            setIsDirtyFiles(true);
          }}           
          onDeleteFile={(file) => {
            setIsDirty(true);
            setIsDirtyFiles(true);
          }}           
          onUpdateFile={(file) => {
            setIsDirty(true);
            setIsDirtyFiles(true);
          }} 
        />
        <FormControl>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            Texte imatge
          </FormLabel>
          <Input
            className="maintenance-form-field"
            placeholder="Texte imatge"
            name="text_image_1"
            data-type={`string`}
            onChange={handleChange}
            value={form[`text_image_1`] ? form[`text_image_1`] : ``}
            maxLength={255}
          />
        </FormControl>           
      </div>
    );
  };

  const renderDynamicEditor = () => {
    return (
      <div className="news-form-dynamic-editor">
        <FormLabel fontSize="sm" className="maintenance-form-label">
          Contingut dinàmic
        </FormLabel>            
        <DynamicEditor 
          mainId={id}
          modelType={"news"}
          items={form["json_content"] || []} 
          onChange={(updatedItems) => {
            setForm({
              ...form,
              json_content: updatedItems,
            });
          }}
          setIsDirty={setIsDirty}
        />
      </div>
    );
  };

  return (
    <MainLayout isLoading={isLoading} setIsLoading={setIsLoading}>
      <div id="news-form-content">
        <MaintenanceForm 
          primaryKey={"id"}
          primaryValue={id}
          primaryName={primaryName}
          config={{
            submitFields: [ 
              `title`,
              `date`,
              `priority`,
              `author`,
              `subtitle`,
              `category`,
              `url_image_1`,
              `text_image_1`,
              `url_web`,
              `enabled`,
              `json_content`,
            ],
          }}
          menuConfig={{}}
          itemNameSingular={itemNameSingular}
          itemIsMale={false}
          actions={newsActions} 
          setIsLoading={setIsLoading}
          renderForm={renderForm}
          form={form}
          setForm={setForm}
          isDirty={isDirty}
          setIsDirty={setIsDirty}
          ref={childRef}
          isNew={isNew}
          page={"news"}
          getFormError={getFormError}
          alterDataFormBeforeSaving={(dataform) => {
            dataform['json_content'] = JSON.stringify(dataform['json_content'] || []);
          }}
          executeAfterSaving={async (theId) => {
            return new Promise((resolve) => {
              // First promise - Upload main image
              const firstPromise = new Promise((resolveFirst) => {
                if (!isDirtyFiles) {
                  resolveFirst({ success: true });
                } else {
                  dispatch(filesActions.setFiles("news", theId, "1", files_1)).then(
                    () => {
                      resolveFirst({ success: true });
                    },
                    (err) => resolveFirst({ success: false, msg: err })
                  );
                }
              });

              // Second promise - Upload audio and image files from dynamic content
              const secondPromise = new Promise((resolveSecond) => {
                const contentFilesItems = form['json_content'].filter(item => (item.type === 'audio' || item.type === 'image') && item.modified);
                if (contentFilesItems.length === 0) {
                  resolveSecond({ success: true });
                  return;
                }

                let completed = 0;
                let hasError = false;

                contentFilesItems.forEach((item) => {
                  dispatch(filesActions.setFiles("news", theId, item.id2, item.files)).then(
                    () => {
                      completed++;
                      if (completed === contentFilesItems.length && !hasError) {
                        resolveSecond({ success: true });
                      }
                    },
                    (err) => {
                      if (!hasError) {
                        hasError = true;
                        resolveSecond({ success: false, msg: err });
                      }
                    }
                  );
                });
              });

              // Concatenate promises
              Promise.all([firstPromise, secondPromise]).then(
                (results) => {
                  const hasError = results.some(r => !r.success);
                  if (hasError) {
                    resolve({ success: false, msg: results.find(r => !r.success)?.msg });
                  } else {
                    resolve({ success: true });
                  }
                }
              );
            });
          }}
        />
      </div>
    </MainLayout>
  );
};

export default NewsForm;
